import http from './http';

import { createLogger } from '../utils/logger';
import {
  HOME_OWNERSHIP_DTO_FIELDS,
  openCredentialFields,
  sealCredentialFields,
} from './encryption/credentialFields';

const logger = createLogger('homeOwnership');
export interface HomeOwnershipInfoRec {
  id: string;
  userId: string;

  // Property Type
  propertyType?: string;

  // Trust/Attorney Information (for ownership)
  inTrust?: string;
  deedLocation?: string; // Note: stored in vault
  trustLocation?: string; // Note: stored in vault
  trustAttorneyName?: string;
  trustAttorneyPracticeName?: string;
  trustAttorneyOfficePhone?: string;
  trustAttorneyCellPhone?: string;
  trustAttorneyEmail?: string;
  trustAttorneyStreet?: string;
  trustAttorneyCity?: string;
  trustAttorneyState?: string;
  trustAttorneyZip?: string;
  trustAttorneySpecialInstructions?: string;

  // Mortgage/Lender Information (for ownership)
  lenderName?: string;
  lenderAccountNumber?: string;
  lenderPhone?: string;
  lenderEmail?: string;
  lenderWebsite?: string;
  lenderHasOnlinePortal?: boolean;
  monthlyPaymentAmount?: string;
  monthlyPaymentDate?: string;
  terminationDate?: string;

  // Rental Information (for rent)
  landlordName?: string;
  landlordPhone?: string;
  landlordEmail?: string;
  propertyManagementCompany?: string;
  managementPhone?: string;
  managementEmail?: string;
  leaseStartDate?: string;
  leaseEndDate?: string;
  monthlyRentAmount?: string;
  securityDepositAmount?: string;
  petDepositAmount?: string;
  rentersInsuranceProvider?: string;
  rentersInsurancePolicyNumber?: string;

  // Common fields
  specialInstructions?: string;

  // Vault-stored credentials (these won't be returned in API responses for security)
  username?: string;
  password?: string;

  isLocked?: boolean;
}

class HomeOwnershipInfoService {
  async getAllHomeOwnershipInfo(): Promise<HomeOwnershipInfoRec[]> {
    const response = await http.get('/api/home-ownership-info');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch home ownership information');
    }

    return response.data.data || [];
  }

  async getHomeOwnershipInfoById(id: string): Promise<HomeOwnershipInfoRec | null> {
    try {
      const response = await http.get(`/api/home-ownership-info/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch home ownership information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching home ownership information by ID:', error);
      return null;
    }
  }

  async createHomeOwnershipInfo(homeOwnershipData: Partial<HomeOwnershipInfoRec>): Promise<HomeOwnershipInfoRec> {
    // K5: the portal login travels inside this DTO; encrypt it on the device.
    const body = await sealCredentialFields(homeOwnershipData as Record<string, unknown>, HOME_OWNERSHIP_DTO_FIELDS);
    const response = await http.post('/api/home-ownership-info', body);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save home ownership information');
    }

    return response.data.data;
  }

  async updateHomeOwnershipInfo(id: string, homeOwnershipData: Partial<HomeOwnershipInfoRec>): Promise<HomeOwnershipInfoRec> {
    const body = await sealCredentialFields(homeOwnershipData as Record<string, unknown>, HOME_OWNERSHIP_DTO_FIELDS);
    const response = await http.patch(`/api/home-ownership-info/${id}`, body);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update home ownership information');
    }

    return response.data.data;
  }

  async deleteHomeOwnershipInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/home-ownership-info/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete home ownership information');
    }
  }

  async lockHomeOwnershipInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for home ownership ID:', id);
    const response = await http.post(`/api/home-ownership-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock home ownership information');
    }
  }

  async unlockHomeOwnershipInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/home-ownership-info/${id}/unlock`, { granteeEmails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock home ownership information');
    }
  }

  async getHomeOwnershipAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/home-ownership-info/${id}/access-grants`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }

  async getHomeOwnershipVaultData(id: string): Promise<{
    username?: string;
    password?: string;
    trustLocation?: string;
    deedLocation?: string;
  } | null> {
    try {
      const response = await http.get(`/api/home-ownership-info/${id}/vault-data`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch vault data');
      }

      const data = response.data.data;
      if (!data) return null;
      // K5: decrypt with the owner's key.
      return await openCredentialFields(data, HOME_OWNERSHIP_DTO_FIELDS, { ownerUserId: data.ownerUserId });
    } catch (error) {
      logger.error('Error fetching vault data:', error);
      return null;
    }
  }
}

export const homeOwnershipInfoService = new HomeOwnershipInfoService();
export default homeOwnershipInfoService;